import { ART_STYLE_DEFAULT, ART_WHAT_MAX, ART_STYLE_MAX } from "./artDraft";

/**
 * 🔄 **옆모습·뒷모습** — 걷기를 정면 그림으로만 굽던 문제를 푸는 조각.
 *
 * ## 무엇이 어긋나 있었나
 * 걷기 굽기(`bakeWalk`)는 방향마다 `<키>.side.still` 같은 **정지 그림**이 있으면 그걸 쓰도록
 * 이미 짜여 있었다. 그런데 **그 키를 만드는 화면이 없었다.** 그래서 세 방향이 전부 정면
 * 그림으로 구워졌고, 아이가 옆으로 걸으면 얼굴이 계속 이쪽을 본다.
 * 이 저장소가 거듭 겪은 「두 곳이 어긋났는데 아무 에러도 안 난다」의 또 한 사례다.
 *
 * ## 여기 두는 이유
 * ① 프롬프트(무엇을 그릴지)와 ② 굽기 계획(어느 그림에서 구울지)은 **판정**이다.
 * 화면마다 다시 짜면 조용히 갈린다 — 판정은 shared 한 곳(CLAUDE.md).
 */

/** 걷기가 있는 세 방향. 왼쪽은 `.side` 를 좌우로 뒤집어 쓴다(WorldScene `setFlipX`). */
export type HeroWalkDir = ".down" | ".side" | ".up";

/** 정지 그림 키 — `<히어로키><방향>.still`. 걷기 굽기가 읽는 이름과 **같아야** 한다. */
export const HERO_STILL_SUFFIX = ".still";
export function heroStillKey(baseKey: string, sfx: HeroWalkDir): string {
  return `${baseKey}${sfx}${HERO_STILL_SUFFIX}`;
}

export interface HeroStillView {
  sfx: HeroWalkDir;
  label: string;
  /** 한 글자짜리 이름 — 「앞·옆·뒤 세 방향을 만들어요」처럼 줄여 말할 때. */
  short: string;
  /** 운영자에게 보여줄 한 줄 — 왜 필요한지. */
  why: string;
  /** AI 에게 보낼 구도. 영어인 이유는 그림 모델이 영어를 훨씬 정확히 듣기 때문이다. */
  frame: string;
}

/**
 * 걷기의 **바탕이 되는 그림** — 방향마다 한 장.
 *
 * 앞모습도 자리가 있다. 「서 있는 모습」으로도 앞걸음을 만들 수 있지만, 그건 팔을 내리고
 * 가만히 선 자세다 — 네 칸이 전부 그 자세라 **걷는다기보다 통통 튀는** 것으로 보인다.
 * 걷는 자세로 한 장 그려 넣으면 같은 굽기로도 훨씬 걷는 것처럼 보인다.
 *
 * 🔴 그래서 셋 다 **걷는 자세**를 요구한다. 이 그림은 「보여 줄 그림」이 아니라
 * **「움직일 바탕」**이다 — 가만히 선 자세를 받아 봐야 눌렀다 펴는 것밖에 못 한다.
 */
export const HERO_STILL_VIEWS: readonly HeroStillView[] = [
  {
    sfx: ".down",
    label: "앞모습",
    short: "앞",
    why: "이쪽으로 걸어올 때 써요. 없으면 서 있는 모습 그대로 통통 튀어요",
    frame:
      "The same child walking towards the viewer, seen from the front, in a gentle mid-stride pose — " +
      "one leg forward, the other back, arms swinging softly. " +
      "Full body from head to feet, centered, filling most of the square. " +
      "No ground, no shadow, no scenery, no other characters.",
  },
  {
    sfx: ".side",
    label: "옆모습",
    short: "옆",
    why: "옆으로 걸을 때 써요. 없으면 정면 얼굴로 옆걸음을 걷게 돼요",
    // 🔴 **오른쪽**을 보게 한다. 왼쪽으로 갈 때는 이 그림을 좌우로 뒤집어 쓰기 때문에
    //    (WorldScene `setFlipX(face === "left")`), 원본이 왼쪽을 보면 방향이 거꾸로 된다.
    frame:
      "The same child walking, seen from the side in full profile, facing to the viewer's right, " +
      "in a gentle mid-stride pose — one leg forward, the other back, arms swinging softly. " +
      "Full body from head to feet, centered, filling most of the square. " +
      "Only one side of the face is visible. No ground, no shadow, no scenery, no other characters.",
  },
  {
    sfx: ".up",
    label: "뒷모습",
    short: "뒤",
    why: "위쪽으로 걸어갈 때 써요. 없으면 뒤로 걸으면서 얼굴이 보여요",
    frame:
      "The same child walking away from the viewer, seen from directly behind, back turned, " +
      "in a gentle mid-stride pose — one leg forward, the other back, arms swinging softly. " +
      "Full body from head to feet, centered, filling most of the square. " +
      "The face is not visible at all — only the back of the head and body. " +
      "No ground, no shadow, no scenery, no other characters.",
  },
];

export function heroStillView(sfx: string): HeroStillView | undefined {
  return HERO_STILL_VIEWS.find((v) => v.sfx === sfx);
}

/**
 * 📝 옆·뒷모습을 그릴 때 AI 에게 보내는 **고칠 수 있는 부분**.
 *
 * 🔴 **참고 그림(서 있는 모습)이 반드시 같이 가야 한다.** 「같은 아이」라고 적어도 그 아이를
 * 보여 주지 않으면 모델은 전혀 다른 아이를 그린다. 그래서 이 칸은 참고 그림을 미리 켜 둔다
 * (`ArtDraftBox` 의 `preset.refs`).
 */
export function heroViewEditable(view: HeroStillView, nick: string, style?: string): string {
  const who = nick.trim().slice(0, ART_WHAT_MAX);
  const look = (style?.trim() || ART_STYLE_DEFAULT).slice(0, ART_STYLE_MAX);
  return [
    `Subject: the exact same child as in the reference image${who ? ` (${who})` : ""}, ` +
      "with the same face, hair, clothes and colors — only the viewing angle changes.",
    view.frame,
    `Style: ${look}`,
  ].join(" ");
}

/** 「무엇을 그릴까요?」 자리에 대신 보여줄 말 — 이 칸은 그릴 것이 정해져 있다. */
export function heroViewSubject(view: HeroStillView, nick: string): string {
  return `${nick.trim() || "이 아이"}의 ${view.label}`;
}

/** 걷기 한 방향을 어떻게 할지. */
export type WalkBakeAction = "make" | "redo" | "keep" | "keep-hand";

export interface WalkBakeStep {
  sfx: HeroWalkDir;
  /** 바탕이 되는 그림의 이름(앞모습·옆모습·뒷모습). */
  label: string;
  /** 줄여 부르는 이름(앞·옆·뒤) — 화면의 한 줄짜리 표에 쓴다. */
  short: string;
  /** 어느 그림에서 굽나(매니페스트 키). */
  from: string;
  /** 그 방향 정지 그림에서 굽나 — 아니면 서 있는 모습에서. */
  fromStill: boolean;
  action: WalkBakeAction;
  /** 화면에 그대로 보여줄 한 줄. **왜 안 만드는지**까지 적는다. */
  why: string;
}

/** 걷기 그림이 「이 정지 그림에서 구워졌다」는 표시. 손으로 올린 것과 가르는 유일한 단서다. */
export interface WalkEntryLike {
  url?: string;
  bakedFrom?: string;
}

/**
 * 🎬 **어느 방향을 다시 구울지 정한다.**
 *
 * 예전 규칙은 「비어 있는 칸만 채운다」 하나뿐이었다. 그래서 이런 함정이 있었다 —
 * 정면으로 한 번 구운 뒤 옆모습 그림을 새로 넣고 다시 누르면 **「이미 다 있어요」**만 나온다.
 * 운영자는 새 그림이 왜 안 쓰이는지 알 길이 없다.
 *
 * 🔴 그렇다고 무조건 덮어쓰면 **손으로 그려 올린 걷기**가 지워진다. 그래서 표시를 본다:
 *  · 표시가 없다 = 손으로 올린 것 → **건드리지 않는다**
 *  · 표시가 다른 그림을 가리킨다(정면에서 구웠는데 이제 옆모습이 있다) → 다시 굽는다
 *  · 표시가 지금 쓸 그림과 같다 → 그대로 둔다(같은 결과가 나온다)
 */
export function walkBakePlan(
  baseKey: string,
  get: (key: string) => WalkEntryLike | undefined,
  dirs: readonly { sfx: string; label: string }[],
): WalkBakeStep[] {
  const out: WalkBakeStep[] = [];
  for (const d of dirs) {
    if (d.sfx === "") continue; // 서 있는 모습은 원본이다
    const sfx = d.sfx as HeroWalkDir;
    const stillKey = heroStillKey(baseKey, sfx);
    const fromStill = !!get(stillKey)?.url;
    // 바탕 그림의 이름은 그 자리의 이름으로 부른다 — 「앞으로 걷기 그림으로」가 아니라 「앞모습 그림으로」.
    const srcName = heroStillView(sfx)?.label ?? d.label;
    const from = fromStill ? stillKey : baseKey;
    const cur = get(baseKey + sfx);
    let action: WalkBakeAction;
    let why: string;
    if (!cur?.url) {
      action = "make";
      why = fromStill ? `${srcName} 그림으로 만들어요` : "서 있는 모습으로 만들어요";
    } else if (!cur.bakedFrom) {
      action = "keep-hand";
      why = "직접 올리신 그림이라 그대로 둬요";
    } else if (cur.bakedFrom === from) {
      action = "keep";
      why = "이미 만들었어요";
    } else {
      action = "redo";
      why = fromStill ? `새 ${srcName} 그림으로 다시 만들어요` : "서 있는 모습으로 다시 만들어요";
    }
    out.push({ sfx, label: srcName, short: heroStillView(sfx)?.short ?? d.label, from, fromStill, action, why });
  }
  return out;
}

/**
 * 실제로 구울 것들. 비어 있으면 부르는 쪽이 「왜 없는지」를 계획에서 읽어 보여 준다.
 *
 * @param force 「이미 만들었어요」인 것도 다시 굽는다 — **굽는 방법이 좋아졌을 때** 필요하다.
 *              예전에 구운 걷기는 계획상 「이미 됐다」라서, 좋아진 방법이 영영 안 닿는다.
 *              🔴 그래도 **직접 올리신 그림은 안 건드린다**(`keep-hand`) — 그건 되돌릴 길이 없다.
 */
export function walkBakeTargets(plan: readonly WalkBakeStep[], force = false): WalkBakeStep[] {
  return plan.filter(
    (s) => s.action === "make" || s.action === "redo" || (force && s.action === "keep"),
  );
}

/**
 * 🗣 **버튼을 누르면 무슨 일이 일어나는지 한 줄로.**
 *
 * 방향별 표는 필요할 때만 펼쳐 보면 된다. 평소에 알아야 할 것은 이 한 줄뿐이다 —
 * 표를 늘 펼쳐 두면 「무엇을 눌러야 하는지」가 글에 묻힌다.
 */
export function walkPlanSummary(plan: readonly WalkBakeStep[]): string {
  const todo = walkBakeTargets(plan);
  if (plan.length === 0) return "먼저 서 있는 모습을 올려 주세요.";
  if (todo.length === 0) return "다 만들었어요. 새로 만들 것이 없어요.";
  const dirs = todo.map((s) => s.short).join("·");
  // 걷는 그림이 하나라도 쓰이면 그렇게 말한다 — 「왜 정면 얼굴로 걷지?」를 미리 막는다.
  const fromStill = todo.filter((s) => s.fromStill).length;
  const how =
    fromStill === todo.length
      ? "넣으신 걷는 그림으로"
      : fromStill === 0
        ? "서 있는 모습으로"
        : "넣으신 그림과 서 있는 모습으로";
  return `${dirs} 방향을 ${how} 만들어요.`;
}

/** 캐릭터 하나가 쓰는 그림 자리 — 걷기 네 칸과, 그 바탕이 된 걷는 그림들. */
export interface HeroOwnedKey {
  key: string;
  label: string;
}

/**
 * 🗑 **이 캐릭터의 그림 전부** — 지울 때 무엇이 사라지는지.
 *
 * 🔴 목록이 **두 곳에 있으면 안 된다.** [➕ 직접 만들기]와 [🧸 캐릭터] 둘 다 지우기를
 * 갖고 있는데, 한쪽만 새 자리(옆·뒷모습)를 알면 **지운 캐릭터의 그림이 매니페스트에 남는다.**
 * 남은 그림은 아무 화면에도 안 나오니 아무도 모르고, 나중에 같은 번호를 다시 쓰면
 * 지운 줄 알았던 옆모습이 되살아난다.
 *
 * @param dirs 걷기 방향 표(화면이 갖고 있는 것). 「서 있는 모습」(`sfx: ""`)도 들어 있다.
 */
export function heroOwnedKeys(
  baseKey: string,
  has: (key: string) => boolean,
  dirs: readonly { sfx: string; label: string }[],
): HeroOwnedKey[] {
  const out: HeroOwnedKey[] = [];
  for (const d of dirs) {
    const key = baseKey + d.sfx;
    if (has(key)) out.push({ key, label: d.label });
  }
  for (const v of HERO_STILL_VIEWS) {
    const key = heroStillKey(baseKey, v.sfx);
    if (has(key)) out.push({ key, label: v.label });
  }
  return out;
}
